/**
 * Shop Deals Collections — Infinite Auto-Scroll Carousel
 * - Continuously auto-scrolls right to left
 * - Pauses on mouse hover, resumes on mouse leave
 * - Supports manual drag / touch swipe
 * - Loops infinitely by duplicating the item list
 */
class ShopDealsCarousel {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.track = wrapper.querySelector('.shop-deals-track');
    if (!this.track || this.track.children.length === 0) return;

    this.speed = parseFloat(wrapper.dataset.speed || '40') / 10; // px per frame baseline
    this.isPaused = false;
    this.isDragging = false;
    this.startX = 0;
    this.scrollPos = 0;
    this.dragMoved = 0;

    this.setupLoop();
    this.bindEvents();
    this.animate = this.animate.bind(this);
    requestAnimationFrame(this.animate);
  }

  // Duplicate items so the track can scroll seamlessly forever
  setupLoop() {
    const originalItems = Array.from(this.track.children);
    // Duplicate twice to ensure enough width for smooth looping on wide screens
    originalItems.forEach((item) => {
      const clone = item.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('tabindex', '-1');
      this.track.appendChild(clone);
    });

    // Wait for layout, then measure single-set width for reset point
    requestAnimationFrame(() => {
      this.singleSetWidth = this.track.scrollWidth / 2;
    });
  }

  bindEvents() {
    // Pause on hover (mouse only, not touch)
    this.wrapper.addEventListener('mouseenter', () => {
      this.isPaused = true;
    });
    this.wrapper.addEventListener('mouseleave', () => {
      this.isPaused = false;
      this.isDragging = false;
    });

    // Manual drag (mouse)
    this.wrapper.addEventListener('mousedown', (e) => this.dragStart(e.pageX));
    window.addEventListener('mousemove', (e) => {
      if (this.isDragging) this.dragMove(e.pageX);
    });
    window.addEventListener('mouseup', () => this.dragEnd());

    // Touch swipe (mobile)
    this.wrapper.addEventListener(
      'touchstart',
      (e) => {
        this.isPaused = true;
        this.dragStart(e.touches[0].pageX);
      },
      { passive: true }
    );
    this.wrapper.addEventListener(
      'touchmove',
      (e) => {
        if (this.isDragging) this.dragMove(e.touches[0].pageX);
      },
      { passive: true }
    );
    this.wrapper.addEventListener('touchend', () => {
      this.dragEnd();
      // Resume auto-scroll shortly after user lets go on mobile
      setTimeout(() => {
        this.isPaused = false;
      }, 1200);
    });

    // Prevent native image drag ghost
    this.track.querySelectorAll('img').forEach((img) => {
      img.addEventListener('dragstart', (e) => e.preventDefault());
    });

    // Pause when tab not visible (performance)
    document.addEventListener('visibilitychange', () => {
      this.tabHidden = document.hidden;
    });
  }

  dragStart(pageX) {
    this.isDragging = true;
    this.isPaused = true;
    this.startX = pageX;
    this.dragMoved = 0;
    this.track.style.transition = 'none';
  }

  dragMove(pageX) {
    const delta = pageX - this.startX;
    this.dragMoved = delta;
    this.startX = pageX;
    this.scrollPos -= delta;
    this.clampAndApply();
  }

  dragEnd() {
    this.isDragging = false;
  }

  clampAndApply() {
    if (this.singleSetWidth) {
      if (this.scrollPos < 0) {
        this.scrollPos += this.singleSetWidth;
      } else if (this.scrollPos >= this.singleSetWidth) {
        this.scrollPos -= this.singleSetWidth;
      }
    }
    this.track.style.transform = `translateX(${-this.scrollPos}px)`;
  }

  animate() {
    if (!this.isPaused && !this.isDragging && !this.tabHidden && this.singleSetWidth) {
      this.scrollPos += this.speed;
      this.clampAndApply();
    }
    requestAnimationFrame(this.animate);
  }
}

function initShopDealsCarousels() {
  document.querySelectorAll('.shop-deals-carousel-wrapper').forEach((wrapper) => {
    if (!wrapper.dataset.initialized) {
      wrapper.dataset.initialized = 'true';
      new ShopDealsCarousel(wrapper);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initShopDealsCarousels);
} else {
  initShopDealsCarousels();
}

// Re-init when a section is added/edited live inside the Theme Editor
document.addEventListener('shopify:section:load', (event) => {
  const wrapper = event.target.querySelector('.shop-deals-carousel-wrapper');
  if (wrapper) {
    delete wrapper.dataset.initialized;
    initShopDealsCarousels();
  }
});